/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context/ThemeContext';
import useHttp from 'hooks/http.hook';
import { CoreAPI_URLs } from 'constants/http.const';
import {
  ActivityLogQueryDTO,
  ActivityLogResponseDTO,
  selectActivityLogs,
  selectActivityLogLoading,
  selectActivityLogPagination,
  selectActivityLogFilters,
  setLogs,
  setLoading,
  setFilters,
} from 'store/slices/activity-log.slice';
import ActivityLogTable from 'components/tables/ActivityLogTable';
import Button from 'components/generals/Button';
import { getFromISOYYYYMMDDDateFormat } from 'utils/date.utils';
import { ActivityLogInterface } from 'store/slices/activity-log.slice';

/**
 * Component for displaying and filtering activity logs for ISO-27001 compliance.
 * Provides audit trail visualization with filters for user, action, entity, date range.
 * Supports pagination and search across activity log entries.
 * 
 * @returns Activity log data content with filters and table
 */
const ActivityLogDataContent: React.FC = () => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isLoading, error, sendRequest] = useHttp();
  const [logs, setLogs] = useState<ActivityLogInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterModel, setFilterModel] = useState<string>('');

  const fetchActivityLogs = async () => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', pageSize.toString());
    
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filterModel) {
      params.append('searchTerm', filterModel);
    }
    try {
      const response = await sendRequest<ActivityLogResponseDTO>(
        `${CoreAPI_URLs.ACTIVITY_LOGS_GET_ALL}?${params.toString()}`,
        undefined,
        'GET'
      );
      if (response) {
        let data: ActivityLogInterface[] = [];
        let total = 0;

        if (Array.isArray(response)) {
          data = response;
          total = response.length;
        } else if (Array.isArray(response.data)) {
          data = response.data;
          total = response.data.length;
        } else if (response.data?.data) {
          data = Array.isArray(response.data.data) ? response.data.data : [];
          total = response.data.total;
        }
        setLogs(data);
        setRowsTotal(total);
        setPageTotal(Math.ceil(total / pageSize));
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setLogs([]);
      setRowsTotal(0);
      setPageTotal(0);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
  }, [page, pageSize, startDate, endDate, filterModel]);
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, filterModel]);

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (endDate && value > endDate) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
  };

  const today = getFromISOYYYYMMDDDateFormat(new Date());
  
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setFilterModel('');
    setPage(1);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('activityLogs.title')}
      </Typography>
      
      <Paper 
        className={`${darkMode ? 'activity-logs-container--dark' : 'activity-logs-container'}`}        
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth
              type="date"
              label={t('activityLogs.startDate')}
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate || today }}
            />
          </Grid>
          
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth
              type="date"
              label={t('activityLogs.endDate')}
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate, max: today }}
              disabled={!startDate}
            />
          </Grid>
          
          <Grid item xs={12} sm={2} md={6} className='clear-filters-container'>
            <Button              
              variant="outlined"
              color="secondary"
              onClick={clearFilters}
              disabled={isLoading}
            >
              {t('activityLogs.clear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <ActivityLogTable
        modelList={logs}
        setModelList={setLogs}
        isLoading={isLoading}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        setModelFilter={setFilterModel}
        canSearchPlaceholder={t('activityLogs.searchPlaceholder')}
      />
    </Box>
  );
};
export default ActivityLogDataContent;